/**
 * StripeCheckoutForm — renders Stripe's PaymentElement for a created order and
 * confirms the payment. The backend webhook is the source of truth for "paid";
 * on success we just send the shopper to the thank-you page, which polls status.
 *
 * Props: publishableKey, clientSecret, orderId, onCancel
 */
import { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

function InnerForm({ orderId, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/thank-you/${orderId}` },
    });
    // If we reach here, confirmPayment failed before redirect (e.g. card error).
    if (error) { setError(error.message || 'Payment failed'); setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="card space-y-4">
      <h2 className="font-bold text-xl">Card details</h2>
      <PaymentElement />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={!stripe || submitting} className="btn-primary w-full py-3 disabled:opacity-50">
        {submitting ? 'Processing…' : 'Pay now'}
      </button>
      <button type="button" onClick={onCancel} className="btn-secondary w-full py-2 text-sm">Back</button>
    </form>
  );
}

export default function StripeCheckoutForm({ publishableKey, clientSecret, orderId, onCancel }) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  if (!clientSecret) return null;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <InnerForm orderId={orderId} onCancel={onCancel} />
    </Elements>
  );
}
